import React from "react";
import { Anchor, Stack, Text } from "@mantine/core";
import { SectionCard } from "@xplatform/design-system";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { MonitoringBreakdown } from "@models/adminMonitoring";
import { AdminTable, col } from "@components/AdminTable";

interface Row {
  model: string;
  unpriced: number;
  events: number;
}

/**
 * Models that ran without a price in the window: their cost is missing from every
 * total above, not zero. Rendered only when there is any.
 */
const UnpricedModels: React.FC<{ byModel: MonitoringBreakdown | undefined }> = ({ byModel }) => {
  const { t } = useTranslation();
  const rows: Row[] = Object.values(
    (byModel?.rows ?? []).filter((r) => r.unpriced_count > 0).reduce<Record<string, Row>>((acc, r) => {
      const row = (acc[r.value] ??= { model: r.value, unpriced: 0, events: 0 });
      row.unpriced += r.unpriced_count;
      row.events += r.event_count;
      return acc;
    }, {}),
  ).sort((a, b) => b.unpriced - a.unpriced);
  if (!rows.length) return null;

  return (
    <SectionCard title={t("admin.insights.unpricedModels.title")}>
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          {t("admin.insights.unpricedModels.description")}{" "}
          <Anchor component={Link} to="/admin/models?price=none" size="sm">{t("admin.insights.unpricedModels.openCatalog")}</Anchor>
        </Text>
        <AdminTable
          columns={[
            col.text<Row>("model", t("admin.insights.columns.model"), (r) => r.model, { mono: true }),
            col.number<Row>("unpriced", t("admin.insights.unpriced"), (r) => r.unpriced),
            col.number<Row>("events", t("admin.insights.columns.events"), (r) => r.events),
          ]}
          data={rows}
          getRowKey={(r) => r.model}
        />
      </Stack>
    </SectionCard>
  );
};

export default UnpricedModels;
